import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { BelongingFooter } from '../components/ui/belonging-footer';
import { EcosystemNav } from '../components/ui/ecosystem-nav';
import { toast } from 'sonner';
import { CommunityIcon, SeedIcon, ArrowIcon, AlertIcon } from '../components/ui/icons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function JoinProject() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [joinCode, setJoinCode] = useState(searchParams.get('code') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const savedName = localStorage.getItem('blissabode_user_name');
    if (savedName) {
      setUserName(savedName);
    }
  }, []);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!userName.trim() || !joinCode.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      localStorage.setItem('blissabode_user_name', userName.trim());

      const response = await fetch(`${API_URL}/api/projects/${projectId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          join_code: joinCode.trim().toUpperCase(),
          user_name: userName.trim(),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to join project');
      }

      toast.success('Welcome to the community!');
      navigate(`/compass/${projectId}`);
    } catch (err) {
      console.error('Join error:', err);
      setError(err.message || 'Failed to join project');
      toast.error(err.message || 'Failed to join');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-[var(--primary)] flex items-center justify-center"
            >
              <CommunityIcon size={40} className="text-white" />
            </motion.div>
            <h1 className="font-heading text-3xl text-[var(--fg)] mb-3">Join a Charter</h1>
            <p className="text-[var(--fg)]/70 max-w-sm mx-auto">
              You've been invited to collaborate on a community charter. Enter your details to join.
            </p>
            <p className="text-xs text-[var(--primary)] mt-2">
              Part of the Belonging Sessions Family
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SeedIcon size={20} className="text-[var(--primary)]" />
                Enter Your Details
              </CardTitle>
              <CardDescription>
                Join this community charter project
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleJoin} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[var(--fg)] mb-2 block">
                    Your Name
                  </label>
                  <Input
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="What should we call you?"
                    data-testid="join-name-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-[var(--fg)] mb-2 block">
                    Join Code
                  </label>
                  <Input
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    placeholder="Enter 6-digit code"
                    className="font-mono tracking-widest text-center text-lg"
                    maxLength={6}
                    data-testid="join-code-input"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-[var(--danger)]/10 rounded-lg text-[var(--danger)] text-sm">
                    <AlertIcon size={16} className="flex-shrink-0" />
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading || !userName.trim() || !joinCode.trim()}
                  data-testid="join-submit-button"
                >
                  {isLoading ? (
                    'Joining...'
                  ) : (
                    <>
                      Join the Community
                      <ArrowIcon size={16} />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t border-[var(--muted)]/50">
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => navigate('/')}
                  data-testid="go-home-button"
                >
                  Go to Dashboard Instead
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="mt-8 text-center">
            <div className="flex justify-center gap-2 mb-3">
              {['Belonging', 'Agency', 'Security', 'Trust'].map((pillar) => (
                <span 
                  key={pillar}
                  className="text-xs px-2 py-0.5 rounded-full bg-[var(--sky)] text-[var(--fg)]/60"
                >
                  {pillar}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
      <EcosystemNav currentSite="community-forge" />
    </div>
  );
}
